/**
 * Retry utility for handling API calls with automatic retry mechanism
 */

// Default retry configuration
const DEFAULT_RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  backoffMultiplier: 2, // exponential backoff
  retryableErrors: [
    'Network Error',
    'timeout',
    'ECONNABORTED',
    'ENOTFOUND',
    'ECONNREFUSED',
    'ETIMEDOUT'
  ]
};

/**
 * Check if an error is retryable
 * @param {Error} error - The error to check
 * @param {Array} retryableErrors - List of retryable error patterns
 * @returns {boolean}
 */
function isRetryableError(error, retryableErrors = DEFAULT_RETRY_CONFIG.retryableErrors) {
  if (!error) return false;
  
  const errorMessage = error.message || '';
  const errorCode = error.code || '';
  const statusCode = error.response?.status;
  
  // Check for network/connection errors
  const isNetworkError = retryableErrors.some(pattern => 
    errorMessage.includes(pattern) || errorCode.includes(pattern)
  );
  
  // Check for specific HTTP status codes that should be retried
  const isRetryableStatusCode = statusCode && (
    statusCode >= 500 || // Server errors
    statusCode === 408 || // Request timeout
    statusCode === 429    // Too many requests
  );
  
  return isNetworkError || isRetryableStatusCode;
}

/**
 * Sleep for a specified amount of time
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise}
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff
 * @param {Function} fn - Function to retry
 * @param {Object} config - Retry configuration
 * @returns {Promise}
 */
export async function retryWithBackoff(fn, config = {}) {
  const {
    maxRetries,
    retryDelay,
    backoffMultiplier,
    retryableErrors,
    onRetry,
    onError
  } = { ...DEFAULT_RETRY_CONFIG, ...config };
  
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await fn();
      return result;
    } catch (error) {
      lastError = error;
      
      // Don't retry if it's the last attempt
      if (attempt === maxRetries) {
        break;
      }
      
      // Don't retry if error is not retryable
      if (!isRetryableError(error, retryableErrors)) {
        break;
      }
      
      // Calculate delay with exponential backoff
      const delay = retryDelay * Math.pow(backoffMultiplier, attempt);
      
      // Call retry callback if provided
      if (onRetry) {
        onRetry(error, attempt + 1, delay);
      }
      
      console.warn(`Retry attempt ${attempt + 1}/${maxRetries} failed:`, error.message);
      console.warn(`Retrying in ${delay}ms...`);
      
      // Wait before retrying
      await sleep(delay);
    }
  }
  
  // Call error callback if provided
  if (onError) {
    onError(lastError, maxRetries);
  }
  
  throw lastError;
}

/**
 * Create a retry wrapper for API functions
 * @param {Function} apiFunction - The API function to wrap
 * @param {Object} retryConfig - Retry configuration
 * @returns {Function}
 */
export function createRetryWrapper(apiFunction, retryConfig = {}) {
  return async (...args) => {
    return retryWithBackoff(
      () => apiFunction(...args),
      retryConfig
    );
  };
}

/**
 * Retry configuration for vehicle-related API calls
 */
export const VEHICLE_API_RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 2000,
  backoffMultiplier: 1.5,
  onRetry: (error, attempt, delay) => {
    console.log(`🔄 Araç verileri yüklenirken hata (${attempt}. deneme): ${error.message}`);
    console.log(`⏱️ ${delay}ms sonra tekrar denenecek...`);
  },
  onError: (error, maxRetries) => {
    console.error(`❌ ${maxRetries} deneme sonrası araç verileri yüklenemedi:`, error);
  }
};

/**
 * Retry configuration for recommendation API calls
 */
export const RECOMMENDATION_API_RETRY_CONFIG = {
  maxRetries: 2,
  retryDelay: 1500,
  backoffMultiplier: 2,
  onRetry: (error, attempt, delay) => {
    console.log(`🔄 Araç önerisi hesaplanırken hata (${attempt}. deneme): ${error.message}`);
    console.log(`⏱️ ${delay}ms sonra tekrar denenecek...`);
  },
  onError: (error, maxRetries) => {
    console.error(`❌ ${maxRetries} deneme sonrası araç önerisi hesaplanamadı:`, error);
  }
};

export default {
  retryWithBackoff,
  createRetryWrapper,
  isRetryableError,
  VEHICLE_API_RETRY_CONFIG,
  RECOMMENDATION_API_RETRY_CONFIG
};